/** Клиент раздела «Грузоместа»: коробки портала и короба/паллеты с упаковки. */
import { api } from "@/api/client";

export interface BoxItemView {
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_ref: string;
  characteristic_name: string;
  partia_ref: string;
  partia_name: string;
  qty_base: number;
  gtin: string;
}

export interface BoxRef {
  id: string;
  sscc: string;
  status: string;
}

/** Откуда грузоместо: `portal` — коробка склада, `packing` — с упаковки (chz-station). */
export type CargoOrigin = "portal" | "packing";

export interface BoxView {
  id: string;
  sscc: string;
  status: string;
  origin: CargoOrigin;
  kind: "box";
  /** Статус словами — с бэка, экран не сочиняет. */
  status_text: string;
  characteristic_ref: string;
  characteristic_name: string;
  warehouse_name: string;
  comment: string;
  printed: boolean;
  created_at: string | null;
  items: BoxItemView[];
  came_from: BoxRef[];
  went_to: BoxRef[];
}

export interface ScanItemOut {
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_ref: string;
  characteristic_name: string;
  partia_ref: string;
  partia_name: string;
  qty_base: number;
  gtin: string;
}

export interface ScanOut {
  raw: string;
  kind: string;
  source: string;
  matched: boolean;
  items: ScanItemOut[];
  needs_characteristic: boolean;
  needs_partia: boolean;
  characteristic_options: { ref: string; name: string }[];
  message: string;
  box: BoxView | null;
  /** Откуда грузоместо; пусто — отсканирован товар, а не грузоместо. */
  origin: CargoOrigin | "";
  /** Короб/паллета с упаковки — карточка целиком (`matched=false`: в сборку не идёт). */
  cargo: CargoView | null;
}

export interface CargoItem {
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_ref: string;
  characteristic_name: string;
  partia_ref: string;
  partia_name: string;
  /** Принято на упаковке. */
  quantity: number;
  /** Сейчас (минус вынутые складом). */
  left: number;
}

export interface CargoPiece {
  code: string;
  taken: boolean;
  taken_at: string | null;
  taken_section: string;
  taken_section_title: string;
  taken_ref: string;
  taken_by: string;
}

export type CargoStatus = "whole" | "opened" | "empty" | "voided" | "dissolved";

export interface CargoRow {
  origin: "packing";
  kind: "box" | "pallet";
  code: string;
  status: CargoStatus;
  status_text: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_name: string;
  partia_name: string;
  quantity: number;
  left: number;
  pallet_code: string;
  boxes_count: number;
  received_at: string | null;
}

export interface CargoEvent {
  at: string | null;
  kind: string;
  text: string;
  section_title: string;
  user_name: string;
}

export type CargoAction = "remove_from_pallet" | "dissolve" | "take_pieces";

/** Карточка короба/паллеты с упаковки. `actions` пуст — только смотреть. */
export interface CargoView {
  origin: "packing";
  kind: "box" | "pallet";
  code: string;
  status: CargoStatus;
  status_text: string;
  void_reason: string;
  quantity: number;
  left: number;
  items: CargoItem[];
  pallet_code: string;
  pieces: CargoPiece[];
  boxes: CargoRow[];
  history: CargoEvent[];
  station: string;
  packed_by_name: string;
  packed_at: string | null;
  received_at: string | null;
  actions: CargoAction[];
}

/**
 * Короба и паллеты с упаковки: без запроса — последние (GET), с запросом —
 * поиск POST-ом.
 *
 * ⚠️ Запрос — в ТЕЛЕ, не в `?q=`: в поле сканируют код штуки ЧЗ с
 * криптохвостом, а строка адреса оседает в access-логах (uvicorn, Traefik) и
 * оттуда в Loki.
 */
export async function listCargo(params: { q?: string } = {}): Promise<CargoRow[]> {
  const q = (params.q ?? "").trim();
  if (!q) {
    const { data } = await api.get<CargoRow[]>("/boxes/cargo");
    return data;
  }
  const { data } = await api.post<CargoRow[]>("/boxes/cargo/search", { q });
  return data;
}

export async function getCargo(code: string): Promise<CargoView> {
  const { data } = await api.get<CargoView>(`/boxes/cargo/${encodeURIComponent(code)}`);
  return data;
}

export async function removeCargoFromPallet(code: string): Promise<CargoView> {
  const { data } = await api.post<CargoView>(
    `/boxes/cargo/${encodeURIComponent(code)}/remove-from-pallet`,
  );
  return data;
}

export async function dissolveCargoPallet(code: string): Promise<CargoView> {
  const { data } = await api.post<CargoView>(`/boxes/cargo/${encodeURIComponent(code)}/dissolve`);
  return data;
}

/** Вынуть штуку из короба упаковки: сырой код ЧЗ как со сканера. */
export async function takeCargoPiece(code: string, raw: string): Promise<CargoView> {
  const { data } = await api.post<CargoView>(
    `/boxes/cargo/${encodeURIComponent(code)}/take-pieces`,
    { raw },
  );
  return data;
}

/** Что вынимаем: строка коробки + количество. Остальное наследуется. */
export interface TakeIn {
  nomenclature_ref: string;
  partia_ref?: string;
  characteristic_ref?: string;
  qty_base: number;
}

export async function listBoxes(params: { q?: string; status?: string } = {}): Promise<BoxView[]> {
  const { data } = await api.get<BoxView[]>("/boxes", { params });
  return data;
}

export async function getBox(id: string): Promise<BoxView> {
  const { data } = await api.get<BoxView>(`/boxes/${id}`);
  return data;
}

export async function scanCode(body: {
  code: string;
  scan_kind?: string;
  ean13?: string;
  gtin?: string;
  serial?: string;
  batch?: string;
  characteristic_ref?: string;
  qty?: number | null;
}): Promise<ScanOut> {
  const { data } = await api.post<ScanOut>("/boxes/scan", body);
  return data;
}

export async function createBox(body: {
  items: (ScanItemOut & { qty_base: number })[];
  comment?: string;
  /**
   * Завести коробку, даже если состав совпал с полным заводским коробом.
   *
   * Портал видит только СОСТАВ, а не тару: шесть штук в заводском коробе и
   * шесть в случайной коробке для него неразличимы. Решает человек.
   */
  force?: boolean;
  /**
   * Сырые коды штук ЧЗ, отсканированные в коробку (в ТЕЛЕ: в них
   * криптохвост). Штука из короба упаковки на бэке отмечается вынутой в той же
   * транзакции — иначе одна штука числилась бы в двух грузоместах.
   */
  piece_codes?: string[];
}): Promise<BoxView> {
  const { data } = await api.post<BoxView>("/boxes", body);
  return data;
}

/**
 * Напечатать наклейку ПОЛНОГО заводского короба.
 *
 * Коробку в портале не заводит: у такого короба есть код групповой упаковки,
 * который сам говорит, что внутри и сколько. Тело то же, что у `createBox` —
 * экран уже собрал состав.
 */
export async function printFactoryLabel(body: {
  items: (ScanItemOut & { qty_base: number })[];
  /** Сырые коды штук ЧЗ состава — как у `createBox`: штуки из коробов
   *  упаковки бэк вынимает в той же транзакции (иначе двойной учёт). */
  piece_codes?: string[];
}): Promise<{ unit_name: string; gtin: string; qty: string }> {
  const { data } = await api.post("/boxes/factory-label", body);
  return data;
}

/**
 * Взять из короба.
 *
 * `intoBox` — положить взятое в НОВУЮ коробку. По умолчанию нет: взятое
 * обычно уезжает в заказ товаром, и номер ему не нужен. `children` в ответе
 * тогда пустой — это норма, а не ошибка.
 */
export async function takeFromBox(
  id: string,
  parts: { items: TakeIn[] }[],
  opts: { intoBox?: boolean; comment?: string } = {},
): Promise<{ parent: BoxView; children: BoxView[] }> {
  const { data } = await api.post(`/boxes/${id}/take`, {
    parts,
    comment: opts.comment ?? "",
    into_box: opts.intoBox ?? false,
  });
  return data;
}

export async function mergeBoxes(boxIds: string[], comment = ""): Promise<BoxView> {
  const { data } = await api.post<BoxView>("/boxes/merge", { box_ids: boxIds, comment });
  return data;
}

export async function printBoxLabel(id: string): Promise<BoxView> {
  const { data } = await api.post<BoxView>(`/boxes/${id}/label`);
  return data;
}

export async function cancelBox(id: string): Promise<BoxView> {
  const { data } = await api.post<BoxView>(`/boxes/${id}/cancel`);
  return data;
}

export interface BoxesConfig {
  scan_method: "tsd" | "camera" | "both";
  printer_ready: boolean;
  printer_problem: string;
}

export async function getBoxesConfig(): Promise<BoxesConfig> {
  const { data } = await api.get<BoxesConfig>("/boxes/config");
  return data;
}
